'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { LinkData } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { BAR_CLEARANCE } from './bottom-bar-size';

/** One category root: its row of the list (level 1) and its panel (level 2), rendered on the server. */
export type MobileCategory = {
  value: string;
  name: string;
  /** „Všetky tonery (n)“ → the root page. */
  all: LinkData | null;
  row: ReactNode;
  panel: ReactNode;
};

const FOCUS = 'focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Top of the view = the bottom edge of the sticky site header (61 px on phones), minus its 1 px
 * line, which the view's own top line replaces – also in the stuck state, where the header shows
 * a shadow instead (Figma 290:37677 keeps the line).
 */
function useHeaderBottom(isOpen: boolean) {
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const header = document.querySelector('header.ea-header');
    const update = () => setTop(Math.max(0, (header?.getBoundingClientRect().bottom ?? 1) - 1));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isOpen]);

  return top;
}

/** The page under the open view does not scroll (the scrollbar width is kept where there is one). */
function useScrollLock(isLocked: boolean) {
  useEffect(() => {
    if (!isLocked) return;
    const root = document.documentElement;
    const { overflow, paddingRight } = root.style;
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflow = 'hidden';
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
    return () => {
      root.style.overflow = overflow;
      root.style.paddingRight = paddingRight;
    };
  }, [isLocked]);
}

/**
 * Figma 290:37677 → 290:37935: the list of the roots (rows from the desktop menu – the active one
 * green, the first until another is opened); a row slides in its panel with the back row „‹ name“
 * and „Všetky tonery (n) →“; back slides the list in again. The focus follows: the back button on
 * level 2, the row it came from on level 1. Each level scrolls on its own from the top.
 */
function CategoryLevels({ roots }: { roots: MobileCategory[] }) {
  const { t } = useTranslations();
  const [active, setActive] = useState(roots[0]?.value ?? '');
  const [level, setLevel] = useState<'list' | 'root'>('list');
  // Only a level change moves the focus; on open Radix focuses the first row.
  const [hasMoved, setMoved] = useState(false);
  const backRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const root = roots.find(entry => entry.value === active);

  useEffect(() => {
    if (!hasMoved) return;
    if (level === 'root') backRef.current?.focus();
    else listRef.current?.querySelector<HTMLElement>('[data-state="active"]')?.focus();
  }, [hasMoved, level]);

  const go = (next: 'list' | 'root', value = active) => {
    setActive(value);
    setLevel(next);
    setMoved(true);
  };

  // A level change slides the new level in (150 ms); the first list comes with the view's fade.
  const scroller = cn(
    'custom-section min-h-0 flex-1 overflow-y-auto overscroll-contain pt-6',
    BAR_CLEARANCE,
  );

  if (level === 'root' && root) {
    return (
      <div
        key={root.value}
        className={cn(scroller, 'slide-in-from-right-full motion-safe:animate-in')}
      >
        <div className="custom-container flex flex-col gap-6">
          <div className="relative flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 items-center gap-1.5">
              {/* The whole row goes back (::after); the name stays a heading for screen readers. */}
              <button
                ref={backRef}
                type="button"
                aria-label={t('text1026')}
                onClick={() => go('list')}
                className={cn(
                  'shrink-0 rounded-[8px] text-[#127A26] after:absolute after:inset-0',
                  FOCUS,
                )}
              >
                <Icon name="caret-left" size={20} />
              </button>
              <h3 className="nt-h5 min-w-0 text-[#0D110D]">{root.name}</h3>
            </div>
            {root.all && (
              <Link
                href={root.all.href}
                className={cn(
                  'nt-button-m relative inline-flex shrink-0 items-center gap-1 rounded-[8px] text-[#0E6A20] underline-offset-4 hover:underline',
                  FOCUS,
                )}
              >
                {root.all.label}
                <Icon name="arrow-right" size={16} className="text-[#127A26]" />
              </Link>
            )}
          </div>
          {root.panel}
        </div>
      </div>
    );
  }

  return (
    <div
      key="list"
      className={cn(scroller, hasMoved && 'slide-in-from-left-full motion-safe:animate-in')}
    >
      <ul ref={listRef} role="list" className="custom-container flex flex-col">
        {roots.map(entry => (
          <li key={entry.value} className="flex">
            <button
              type="button"
              data-state={entry.value === active ? 'active' : 'inactive'}
              onClick={() => go('root', entry.value)}
              className={cn(
                'group flex min-h-11 w-full items-center gap-2 rounded-xl py-3 text-left text-[#0D110D] data-[state=active]:text-[#0E6A20]',
                FOCUS,
              )}
            >
              {entry.row}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Figma „Open kategórie“ (290:37677, 290:37935) – the view of the bottom bar's „Kategórie“ tab,
 * between the sticky header and the bar (the bar covers its bottom, the padding keeps the end above
 * it). A non-modal Radix dialog rendered in place: the header and the bar stay usable and a tap on
 * them closes it, so does the tab again, Escape (the focus returns to the tab), a link (click
 * delegation – also same-page anchors) and a route change (the parent). Tab cycles inside (Radix
 * focus loop), so the covered page is never focused; it does not scroll either. Closed, nothing is
 * mounted; open, it starts on the list.
 */
export function MobileCategories({
  label,
  roots,
  isOpen,
  onClose,
}: {
  label: string;
  roots: MobileCategory[];
  isOpen: boolean;
  onClose: () => void;
}) {
  const top = useHeaderBottom(isOpen);
  useScrollLock(isOpen);

  // Click delegation only: every target inside is a link or a button with its own keys.
  const closeOnLink = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a[href]')) onClose();
  };

  return (
    <DialogPrimitive.Content
      aria-describedby={undefined}
      onClick={closeOnLink}
      style={{ top }}
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 flex flex-col overflow-hidden border-t border-[#E2E5DF] bg-white outline-none lg:hidden',
        'data-[state=open]:fade-in-0 motion-safe:data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 motion-safe:data-[state=closed]:animate-out',
      )}
    >
      <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
      <CategoryLevels roots={roots} />
    </DialogPrimitive.Content>
  );
}
